import { Button, Offcanvas } from "react-bootstrap";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { useServiceAvailability } from "../context/ServiceAvailabilityContext";
import { formatCurrency } from "../utilities/formatCurrency";
import { CartItem } from "./CartItem";
import { calculateSubtotal } from "../lib/calculator.jsx";
import { useState, useEffect, useCallback } from "react";
import { auth } from "../lib/config/firebaseInit";
import { useNavigate } from "react-router-dom";
import { toast } from "../utilities/toast";
import { X, Cart3, Clock } from "react-bootstrap-icons";

// Exported so Orders.jsx and other consumers can reuse the divider style
export const hrStyle = {
  width: "100%",
  border: 0,
  height: "1px",
  background: "rgba(60,60,67,0.1)",
};

const TOAST_DURATION = 6000;
const BUTTON_DISABLE_DURATION = 6000;

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyCart() {
  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "40px 24px",
    }}>
      <div style={{
        width: 68, height: 68, borderRadius: "50%",
        backgroundColor: "rgba(0,122,255,0.1)",
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: 18,
      }}>
        <Cart3 size={30} color="#007aff" />
      </div>
      <h3 style={{
        fontSize: "1.125rem", fontWeight: 700,
        color: "#1c1c1e", letterSpacing: "-0.01em", marginBottom: 6,
      }}>
        Your cart is empty
      </h3>
      <p style={{
        fontSize: "0.875rem", color: "rgba(60,60,67,0.55)",
        lineHeight: 1.45, maxWidth: 220, margin: 0,
      }}>
        Add items from a store to get started
      </p>
    </div>
  );
}

// ─── Closed banner ────────────────────────────────────────────────────────────
// Same icon + color language as Checkout.jsx's closed-service screen (Clock,
// #FF9500 / rgba(255,149,0,0.1)), scaled down to a banner for this drawer.

function ClosedBanner({ opensAt }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      margin: "12px 20px 0",
      padding: "12px 14px",
      borderRadius: 12,
      backgroundColor: "rgba(255,149,0,0.1)",
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: "50%",
        backgroundColor: "rgba(255,149,0,0.16)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <Clock size={15} color="#FF9500" />
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{
          margin: 0, fontSize: "0.8125rem", fontWeight: 700,
          color: "#1c1c1e", letterSpacing: "-0.01em",
        }}>
          We're currently closed
        </p>
        <p style={{
          margin: 0, fontSize: "0.75rem", color: "rgba(60,60,67,0.6)",
        }}>
          {opensAt ? `We reopen at ${opensAt}` : "Please check back soon"}
        </p>
      </div>
    </div>
  );
}

// ─── ShoppingCart ─────────────────────────────────────────────────────────────

export function ShoppingCart({ isOpen }) {
  const { closeCart, cartItems } = useShoppingCart();
  const { isOpen: isServiceOpen, opensAt: serviceOpensAt } = useServiceAvailability();
  const [total, setTotal] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isToastRunning, setIsToastRunning] = useState(false);
  const navigate = useNavigate();
  const hasItems = cartItems && cartItems.length > 0;

  const calculateTotal = useCallback(async () => {
    setIsCalculating(true);
    try {
      const sum = await calculateSubtotal(cartItems);
      setTotal(sum);
    } catch (error) {
      console.error("Error calculating total:", error);
      toast.error("Failed to calculate cart total");
    } finally {
      setIsCalculating(false);
    }
  }, [cartItems]);

  useEffect(() => {
    calculateTotal();
  }, [calculateTotal]);

  const handleCheckout = useCallback(() => {
    if (auth.currentUser) {
      closeCart();
      navigate("/checkout");
      return;
    }
    navigate("/login");
    setIsToastRunning(true);
    toast.warning("Please sign in to checkout.", TOAST_DURATION);
    const timer = setTimeout(() => setIsToastRunning(false), BUTTON_DISABLE_DURATION);
    return () => clearTimeout(timer);
  }, [closeCart, navigate]);

  return (
    <>
      <style>{`
        .cart-offcanvas-backdrop { z-index: 19999 !important; }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
      <Offcanvas
        show={isOpen}
        onHide={closeCart}
        placement="end"
        style={{ zIndex: 20000, display: "flex", flexDirection: "column" }}
        backdropClassName="cart-offcanvas-backdrop"
      >
        {/* ── Header ──────────────────────────────────────────── */}
        <Offcanvas.Header style={{
          padding: "16px 20px",
          borderBottom: "1px solid rgba(60,60,67,0.08)",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.01em" }}>
                Cart
              </span>
              {hasItems && (
                <span style={{
                  fontSize: "0.75rem", fontWeight: 600,
                  color: "#007aff", backgroundColor: "rgba(0,122,255,0.1)",
                  borderRadius: 999, padding: "1px 8px", lineHeight: "20px",
                }}>
                  {cartItems.length}
                </span>
              )}
            </div>
            <button
              onClick={closeCart}
              style={{
                width: 32, height: 32, borderRadius: "50%",
                border: "none",
                backgroundColor: "rgba(60,60,67,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <X size={16} color="#3c3c43" />
            </button>
          </div>
        </Offcanvas.Header>

        {/* ── Body ────────────────────────────────────────────── */}
        <Offcanvas.Body style={{
          display: "flex", flexDirection: "column",
          padding: 0, overflow: "hidden", flex: 1,
        }}>
          {!isServiceOpen && <ClosedBanner opensAt={serviceOpensAt} />}

          {/* Scrollable items */}
          <div style={{ flex: 1, overflowY: "auto", padding: "4px 20px 0" }}>
            {hasItems ? (
              cartItems.map((item, i) => (
                <div key={item.id}>
                  <CartItem {...item} />
                  {i < cartItems.length - 1 && <hr style={hrStyle} />}
                </div>
              ))
            ) : (
              <EmptyCart />
            )}
          </div>

          {/* Sticky footer — only when cart has items */}
          {hasItems && (
            <div style={{
              flexShrink: 0,
              padding: "16px 20px",
              paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
              borderTop: "1px solid rgba(60,60,67,0.08)",
              backgroundColor: "#fff",
            }}>
              <div style={{
                display: "flex", justifyContent: "space-between",
                alignItems: "center", marginBottom: 14,
              }}>
                <span style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.6)", fontWeight: 500 }}>
                  Subtotal
                </span>
                {isCalculating ? (
                  <div style={{
                    height: 18, width: 64, borderRadius: 6,
                    backgroundColor: "rgba(118,118,128,0.12)",
                    animation: "pulse 1.4s ease-in-out infinite",
                  }} />
                ) : (
                  <span style={{ fontSize: "1rem", fontWeight: 700, color: "#1c1c1e" }}>
                    {formatCurrency(total)}
                  </span>
                )}
              </div>
              <Button
                onClick={handleCheckout}
                disabled={isToastRunning || !isServiceOpen}
                variant="primary"
                style={{
                  width: "100%",
                  borderRadius: 12,
                  padding: "13px",
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  border: "none",
                }}
              >
                {isServiceOpen ? "Checkout" : "Currently Closed"}
              </Button>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
